import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react'

/**
 * Free drag-to-reposition for a `position: fixed` overlay box (desktop only).
 *
 * Returns pointer handlers + an inline `style` (a `transform: translate()`
 * offset that composes on top of whatever fixed anchor the box already has in
 * CSS) so the caller just spreads both onto the box root. The offset persists
 * to `localStorage` under `storageKey`, so a box the user moved stays where
 * they dropped it across reloads.
 *
 * Drag only starts from the box *background*: a pointer-down that lands on an
 * interactive control (button / link / input / media) or directly on a piece
 * of text is ignored, so clicking a button or selecting text never drags the
 * box — only grabbing the empty padding / gaps between elements does.
 */
export interface DraggableResult {
  handlers: {
    onPointerDown?: (e: ReactPointerEvent) => void
    onPointerMove?: (e: ReactPointerEvent) => void
    onPointerUp?: (e: ReactPointerEvent) => void
    onPointerCancel?: (e: ReactPointerEvent) => void
  }
  style: CSSProperties
  dragging: boolean
  /** Reset the box back to its CSS anchor (clears the stored offset). */
  reset: () => void
}

/** True when a drag started on `target` should be suppressed: the pointer
 *  landed on an interactive control, a media element, or a text leaf. */
export function isNonDragTarget(target: EventTarget | null, root: HTMLElement): boolean {
  let el = target as HTMLElement | null
  while (el && el !== root) {
    const tag = el.tagName
    if (/^(BUTTON|A|INPUT|SELECT|TEXTAREA|LABEL|IMG|SVG|PATH|VIDEO|AUDIO|CANVAS)$/.test(tag)) return true
    if (el.getAttribute('role') === 'button') return true
    if (el.isContentEditable) return true
    el = el.parentElement
  }
  // Block clicks that land directly on a text-bearing leaf (a span/h3/p whose
  // only content is text) — grabbing the text must not move the box.
  const t = target as HTMLElement | null
  if (t && t !== root && t.childElementCount === 0 && (t.textContent ?? '').trim().length > 0) return true
  return false
}

export function useDraggable(enabled: boolean, storageKey?: string): DraggableResult {
  const [offset, setOffset] = useState<{ x: number; y: number }>(() => {
    if (storageKey && typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem(storageKey)
        if (raw) {
          const p = JSON.parse(raw)
          if (typeof p?.x === 'number' && typeof p?.y === 'number') return p
        }
      } catch { /* ignore corrupt value */ }
    }
    return { x: 0, y: 0 }
  })
  const [dragging, setDragging] = useState(false)
  const drag = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 })

  // Persist so a moved box stays put across reloads.
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return
    try { window.localStorage.setItem(storageKey, JSON.stringify(offset)) } catch { /* quota / private mode */ }
  }, [offset, storageKey])

  const onPointerDown = useCallback((e: ReactPointerEvent) => {
    if (!enabled) return
    if (isNonDragTarget(e.target, e.currentTarget as HTMLElement)) return
    const d = drag.current
    d.active = true
    d.startX = e.clientX
    d.startY = e.clientY
    d.baseX = offset.x
    d.baseY = offset.y
    setDragging(true)
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* unsupported */ }
  }, [enabled, offset])

  const onPointerMove = useCallback((e: ReactPointerEvent) => {
    const d = drag.current
    if (!d.active) return
    setOffset({ x: d.baseX + (e.clientX - d.startX), y: d.baseY + (e.clientY - d.startY) })
  }, [])

  const onPointerUp = useCallback((e: ReactPointerEvent) => {
    const d = drag.current
    if (!d.active) return
    d.active = false
    setDragging(false)
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* noop */ }
  }, [])

  const reset = useCallback(() => setOffset({ x: 0, y: 0 }), [])

  if (!enabled) {
    return { handlers: {}, style: {}, dragging: false, reset }
  }

  return {
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
    style: {
      transform: offset.x === 0 && offset.y === 0 ? undefined : `translate(${offset.x}px, ${offset.y}px)`,
      touchAction: 'none',
      cursor: dragging ? 'grabbing' : undefined,
      userSelect: dragging ? 'none' : undefined,
    },
    dragging,
    reset,
  }
}
